/**
 * 네이버 프리미엄 로그 분석 유틸리티 함수
 * https://naver.github.io/conversion-tracking/
 */

import { hasPurchaseEventSent, markPurchaseEventSent } from "./purchaseDedup";

// 전환 아이템 타입
interface ConversionItem {
  id: string;         // 상품 고유 ID
  name: string;       // 상품명
  category?: string;  // 카테고리
  quantity: number;   // 수량
  payAmount: number;  // 결제 금액
  option?: string;    // 옵션 정보
}

// 전환 이벤트 타입
interface ConversionEvent {
  type: 'purchase' | 'sign_up' | 'add_to_cart' | 'lead' | 'schedule' | string;
  id?: string;        // 주문번호 등 고유 ID
  items?: ConversionItem[];
  value?: string;     // 총 금액 (문자열)
}

declare global {
  interface Window {
    wcs: {
      inflow: (domain?: string) => void;
      trans: (conv: ConversionEvent) => void;
    };
    wcs_add: { wa: string };
    wcs_do: () => void;
    _nasa?: Record<string, unknown>;
  }
}

/**
 * 네이버 프리미엄 로그 공통 스크립트 초기화 + 초기 PV 전송.
 * wcslog.js 로드 완료(onLoad) 시점에 호출하여 wcs 전역이 준비된 뒤 실행되도록 한다.
 * (beforeInteractive 인라인 <Script>는 React 19에서 raw script 경고를 유발하므로 제거)
 */
export function initNaverPremiumLog(accountId: string): void {
  try {
    if (typeof window === 'undefined') return;
    window.wcs_add = { wa: accountId };
    if (!window._nasa) window._nasa = {};
    if (window.wcs) {
      window.wcs.inflow('edugamja.com');
      window.wcs_do();
    }
  } catch {
    // wcs 전역 미준비/접근 차단 시 무시 (SPA 라우트 변경 시 재전송됨)
  }
}

/**
 * 네이버 프리미엄 로그 초기화 여부 확인
 */
export function isNaverLogInitialized(): boolean {
  return typeof window !== 'undefined' && !!window.wcs;
}

/**
 * 페이지뷰(PV) 이벤트 전송
 */
export function sendNaverPageView(): void {
  try {
    if (typeof window !== 'undefined' && window.wcs && window.wcs_do) {
      window.wcs_do();

      if (process.env.NODE_ENV === 'development') {
        console.log('[Naver Premium Log] PageView sent');
      }
    }
  } catch {
    // localStorage 접근 차단 등으로 인한 SecurityError 무시
  }
}

/**
 * 전환 이벤트 전송
 */
export function sendNaverConversionEvent(event: ConversionEvent): void {
  try {
    if (typeof window !== 'undefined' && window.wcs && window.wcs.trans) {
      window.wcs.trans(event);

      if (process.env.NODE_ENV === 'development') {
        console.log('[Naver Premium Log] Conversion event sent:', event);
      }
    }
  } catch {
    // localStorage 접근 차단 등으로 인한 SecurityError 무시
  }
}

/**
 * 구매 완료 이벤트
 */
export function trackNaverPurchase(params: {
  orderId: string;
  productId: string;
  productName: string;
  amount: number;
  quantity?: number;
  category?: string;
}): void {
  const { orderId, productId, productName, amount, quantity = 1, category } = params;

  const conversionEvent: ConversionEvent = {
    type: 'purchase',
    id: orderId,
    items: [
      {
        id: productId,
        name: productName,
        category: category || '이용권',
        quantity,
        payAmount: amount,
      },
    ],
    value: String(amount),
  };

  sendNaverConversionEvent(conversionEvent);
}

/**
 * 회원가입 완료 이벤트
 */
export function trackNaverSignUp(params?: { id?: string }): void {
  const conversionEvent: ConversionEvent = {
    type: 'sign_up',
    id: params?.id,
  };

  sendNaverConversionEvent(conversionEvent);
}

/**
 * 리드 생성 이벤트 (관심 표시, 문의 등)
 */
export function trackNaverLead(params?: { id?: string }): void {
  const conversionEvent: ConversionEvent = {
    type: 'lead',
    id: params?.id,
  };

  sendNaverConversionEvent(conversionEvent);
}

// localStorage 키
const NAVER_LOG_REGISTRATION_SENT_KEY = 'naver_log_registration_sent';
const NAVER_LOG_PURCHASE_PREFIX = 'naver_log_purchase_';

/**
 * 회원가입 이벤트 전송 여부 확인
 */
export function hasNaverRegistrationSent(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(NAVER_LOG_REGISTRATION_SENT_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * 회원가입 이벤트 전송 완료 표시
 */
export function markNaverRegistrationSent(): void {
  try {
    if (typeof window !== 'undefined') {
      localStorage.setItem(NAVER_LOG_REGISTRATION_SENT_KEY, 'true');
    }
  } catch {
    // localStorage 접근 불가 시 무시
  }
}

/**
 * 특정 주문의 구매 이벤트 전송 여부 확인
 */
export function hasNaverPurchaseSent(orderId: string): boolean {
  try {
    if (hasPurchaseEventSent('naver', orderId)) return true;
    if (typeof window === 'undefined') return false;

    const legacySent = window.localStorage.getItem(`${NAVER_LOG_PURCHASE_PREFIX}${orderId}`) === 'true';
    if (legacySent) {
      if (markPurchaseEventSent('naver', orderId)) {
        window.localStorage.removeItem(`${NAVER_LOG_PURCHASE_PREFIX}${orderId}`);
      }
    }
    return legacySent;
  } catch {
    return false;
  }
}

/**
 * 특정 주문의 구매 이벤트 전송 완료 표시
 */
export function markNaverPurchaseSent(orderId: string): void {
  markPurchaseEventSent('naver', orderId);
}
